import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from "redux-persist";
import storage from "redux-persist/lib/storage"; // defaults to localStorage for web
import userReducer from "./slices/user.slice";
import globalReducer from "./slices/global.slice";
import configurationReducer from "./slices/baseSetup.slice";
import staffReducer from "./slices/staff.slice";
import stockReducer from "./slices/stock.slice";
import { setItem } from "../utils/localStorage";
import vendorReducer from "./slices/vendor.slice";
import filterSortingReducer from "./slices/filterSorting.slice";
import filterOptionsReducer from "./slices/filterOptions.slice";
import fileUploadReducer from "./slices/fileUpload.slice";
import exportReducer from "./slices/export.slice";
import pinnedMenuReducer from "./slices/pinnedMenu.slice";

// Persist configuration for the user slice, targeting only authentication-related fields
const userPersistConfig = {
  key: "user",
  storage,
  whitelist: ["userInfo", "isAuthenticated", "user", "role", "permissions", "ws_type", "activeAttendance"], // Persist auth-related fields and activeAttendance
};

// Persist configuration for the file upload slice
const fileUploadPersistConfig = {
  key: "fileUpload",
  storage,
  whitelist: ["uploads", "isUploadPanelOpen"], // Persist uploads and panel state
};

// Persist configuration for the export slice
const exportPersistConfig = {
  key: "export",
  storage,
  whitelist: ["exports", "isExportPanelOpen"], // Persist exports and panel state
};

// Persist configuration for the pinned menu slice
const pinnedMenuPersistConfig = {
  key: "pinnedMenu",
  storage,
  whitelist: ["pinnedItems"], // Persist pinned items
};

// Create a reset action
export const resetStore = () => ({
  type: "RESET_STORE",
});

// Wrap reducers with their respective persist configurations
const rootReducer = combineReducers({
  user: persistReducer(userPersistConfig, userReducer),
  global: globalReducer,
  configuration: configurationReducer,
  staff: staffReducer,
  vendor: vendorReducer,
  stock: stockReducer,
  filterSorting: filterSortingReducer,
  filterOptions: filterOptionsReducer,
  fileUpload: persistReducer(fileUploadPersistConfig, fileUploadReducer),
  export: persistReducer(exportPersistConfig, exportReducer),
  pinnedMenu: persistReducer(pinnedMenuPersistConfig, pinnedMenuReducer),
});

// Wrap rootReducer with persistReducer
const store = configureStore({
  reducer: (state, action) => {
    if (action.type === "RESET_STORE") {
      // Clear persisted state
      storage.removeItem("persist:user");
      storage.removeItem("persist:fileUpload");
      storage.removeItem("persist:export");
      storage.removeItem("persist:pinnedMenu");

      // Reset all reducers to their initial state
      return rootReducer(undefined, action);
    }
    return rootReducer(state, action);
  },
  devTools: true,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

store.subscribe(() => {
  setItem("user", store.getState().user.userInfo);
});

const persistor = persistStore(store);

export { store, persistor };
